import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Dice5 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const FAMILY_LABEL = { tradicional: "Tradicional", animalitos: "Animalitos", chance: "Chance / Números" };

export default function LoteriasPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["lottery-types"],
    queryFn: async () => (await api.get("/lottery-types")).data,
  });

  const grouped = useMemo(() => {
    const g = { tradicional: [], animalitos: [], chance: [] };
    for (const it of items) (g[it.family] || (g[it.family] = [])).push(it);
    return g;
  }, [items]);

  const save = useMutation({
    mutationFn: async (payload) => {
      const body = { ...payload };
      body.max_plays_per_ticket = body.max_plays_per_ticket === "" || body.max_plays_per_ticket == null
        ? null : parseInt(body.max_plays_per_ticket, 10);
      return (await api.patch(`/lottery-types/${editing.id}`, body)).data;
    },
    onSuccess: () => {
      toast.success("Lotería actualizada");
      qc.invalidateQueries({ queryKey: ["lottery-types"] });
      setEditing(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  const openEdit = (lt) => {
    setEditing(lt);
    setForm({
      name: lt.name,
      payout_multiplier: lt.payout_multiplier,
      min_bet_bs: lt.min_bet_bs,
      max_bet_bs: lt.max_bet_bs,
      number_length: lt.number_length,
      active: lt.active,
      color: lt.color || "",
      has_zodiac_sign: !!lt.has_zodiac_sign,
      max_plays_per_ticket: lt.max_plays_per_ticket ?? "",
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Loterías</h1>
        <p className="text-sm text-muted-foreground">Configura los juegos disponibles y sus reglas de pago.</p>
      </div>

      {isLoading && <div className="text-muted-foreground">Cargando…</div>}

      {Object.entries(grouped).map(([family, list]) => (
        list.length > 0 && (
          <div key={family} className="space-y-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">{FAMILY_LABEL[family] || family}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {list.map((lt) => (
                <Card key={lt.id} className="bg-card border-border p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg grid place-items-center border border-border" style={{ backgroundColor: (lt.color || "#1FB6A6") + "22", color: lt.color || "#1FB6A6" }}>
                        <Dice5 size={20} />
                      </div>
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {lt.name}
                          {lt.has_zodiac_sign && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-violet-500/40 text-violet-300 bg-violet-500/10" data-testid={`lottery-zodiac-badge-${lt.code}`}>ZODIACAL</span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground font-mono">{lt.code}</div>
                      </div>
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(lt)} data-testid={`lottery-edit-${lt.code}`}><Pencil size={16}/></Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                    <div className="rounded-md border border-border/60 py-2">
                      <div className="text-[10px] uppercase text-muted-foreground">Paga</div>
                      <div className="font-mono text-primary tabular-nums">{lt.payout_multiplier}x</div>
                    </div>
                    <div className="rounded-md border border-border/60 py-2">
                      <div className="text-[10px] uppercase text-muted-foreground">Dígitos</div>
                      <div className="font-mono tabular-nums">{lt.number_length}</div>
                    </div>
                    <div className="rounded-md border border-border/60 py-2">
                      <div className="text-[10px] uppercase text-muted-foreground">Estado</div>
                      <div className={`text-xs font-medium ${lt.active ? "text-emerald-300" : "text-rose-300"}`}>{lt.active ? "Activa" : "Inactiva"}</div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">Bs {lt.min_bet_bs} - {lt.max_bet_bs}</div>
                </Card>
              ))}
            </div>
          </div>
        )
      ))}

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>Editar {editing?.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label>Nombre</Label>
              <Input value={form.name || ""} onChange={(e) => setForm({...form, name: e.target.value})}/>
            </div>
            <div className="space-y-2">
              <Label>Multiplicador de pago</Label>
              <Input type="number" value={form.payout_multiplier || 0} onChange={(e) => setForm({...form, payout_multiplier: parseFloat(e.target.value) || 0})}/>
            </div>
            <div className="space-y-2">
              <Label>Dígitos del número</Label>
              <Input type="number" min={1} max={5} value={form.number_length || 3} onChange={(e) => setForm({...form, number_length: parseInt(e.target.value) || 3})}/>
            </div>
            <div className="space-y-2">
              <Label>Apuesta mínima (Bs)</Label>
              <Input type="number" value={form.min_bet_bs || 0} onChange={(e) => setForm({...form, min_bet_bs: parseFloat(e.target.value) || 0})}/>
            </div>
            <div className="space-y-2">
              <Label>Apuesta máxima (Bs)</Label>
              <Input type="number" value={form.max_bet_bs || 0} onChange={(e) => setForm({...form, max_bet_bs: parseFloat(e.target.value) || 0})}/>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Color (hex)</Label>
              <Input value={form.color || ""} onChange={(e) => setForm({...form, color: e.target.value})}/>
            </div>
            <div className="space-y-2">
              <Label>Máx. jugadas por ticket</Label>
              <Input type="number" min={1} value={form.max_plays_per_ticket ?? ""} placeholder="Global (50)" onChange={(e) => setForm({...form, max_plays_per_ticket: e.target.value})} data-testid="lottery-max-plays-input"/>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={!!form.has_zodiac_sign} onCheckedChange={(v) => setForm({...form, has_zodiac_sign: v})} id="lt-zodiac" data-testid="lottery-zodiac-switch"/>
              <Label htmlFor="lt-zodiac">Signo zodiacal</Label>
            </div>
            <div className="flex items-center gap-2 md:col-span-2">
              <Switch checked={!!form.active} onCheckedChange={(v) => setForm({...form, active: v})} id="lt-active"/>
              <Label htmlFor="lt-active">Activa</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={() => save.mutate(form)} disabled={save.isPending}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
